import React from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { darkModeClasses, getInfoPanelClasses } from '../../utils/darkMode';
import { useSMSComposer } from '../../hooks/useSMSComposer';
import { Member, Town, Event } from '../../types';

interface SMSComposerProps {
  members: Member[];
  towns: Town[];
  selectedTownId: string;
  events: Event[];
}

export const SMSComposer: React.FC<SMSComposerProps> = ({
  members,
  towns,
  selectedTownId,
  events
}) => {
  const {
    message,
    type: messageType,
    selectedEventId,
    eventOptions,
    filteredMembers,
    selectedMembers,
    searchTerm,
    statusFilter,
    sortOption,
    isRefreshing,

    // Actions
    handleTemplateChange,
    updateMessage,
    updateSearchTerm,
    updateStatusFilter,
    updateSortOption,
    toggleMemberSelection,
    selectAllVisibleMembers,
    selectAllMembers,
    handleSendSMS,
    refreshData,
    getRecipientCount,
    getEstimatedCost,
    availableTemplates,
  } = useSMSComposer({ members, towns, selectedTownId, events });

  const selectedTownName = (() => {
    if (selectedTownId === 'no-town-assigned') {
      return 'No Town Assigned';
    }
    if (selectedTownId === 'all') {
      return 'All Towns';
    }
    return towns.find((t: Town) => t.id === selectedTownId)?.name || 'Unknown Town';
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h1 className={`text-xl md:text-2xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <MessageSquare className="h-6 w-6 md:h-7 md:w-7 mr-2 md:mr-3 text-blue-600" />
            <span className="hidden sm:inline">Compose Message - </span>{selectedTownName}
          </h1>
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors text-sm"
          >
            <span className={isRefreshing ? 'animate-spin' : ''}>⟳</span>
            <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
        <p className={`${darkModeClasses.textSecondary} mt-1`}>
          Send bulk SMS to family members using Hubtel or NALO Solutions
        </p>
      </div>

      <div className="w-full space-y-6">
        {/* Compose Message */}
        <div className={`${darkModeClasses.container} p-6`}>
          <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Compose Message</h2>

          <div className="space-y-4">
            {/* Template Selector */}
            <div>
              <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                Message Template
              </label>
              <select
                value={messageType}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                aria-label="Message Template"
              >
                <option value="general">Custom Message</option>
                {availableTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Event Selector */}
            {(messageType.includes('event') || messageType.includes('meeting')) && (
              <div>
                <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                  {messageType.includes('event') ? 'Select Event' : 'Select Meeting'}
                </label>
                <select
                  value={selectedEventId}
                  onChange={(_e) => {
                    // This is handled through the hook by changing template
                    handleTemplateChange(messageType);
                  }}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  aria-label={`${messageType.includes('event') ? 'Select Event' : 'Select Meeting'}`}
                >
                  <option value="">Select an event...</option>
                  {eventOptions.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title} - {event.date} at {event.time}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Message Input */}
            <div>
              <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                Message ({message.length}/160 characters)
              </label>
              <textarea
                value={message}
                onChange={(e) => {
                  const newMessage = e.target.value;
                  if (newMessage.length <= 160) {
                    // Allow direct editing of the message
                    // This will work with templates and allow customization
                    updateMessage(newMessage);
                  }
                }}
                placeholder={messageType !== 'general' ? "Customize your message..." : "Type your message or select a template above..."}
                rows={4}
                maxLength={160}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              <p className={`text-xs ${darkModeClasses.textTertiary} mt-1`}>
                SMS messages are limited to 160 characters. Longer messages will be split.
                <br />
                <em>Select a template to get started, or write your custom message directly.</em>
              </p>
            </div>

            {/* Send Button */}
            <div className={`${getInfoPanelClasses('blue')} border rounded-lg p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    Recipients: {getRecipientCount()} family members
                  </p>
                  <p className="text-sm">
                    Estimated Cost: GHS {getEstimatedCost().toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={handleSendSMS}
                  disabled={!message.trim() || getRecipientCount() === 0}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Send className="h-4 w-4" />
                  <span>Send SMS</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Member Selection */}
        <div className={`${darkModeClasses.container} p-6`}>
          <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Select Family Members</h3>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                Search by Name/Phone
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => updateSearchTerm(e.target.value)}
                placeholder="Search members..."
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => updateStatusFilter(e.target.value)}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                aria-label="Filter by Status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                Sort by
              </label>
              <select
                value={sortOption}
                onChange={(e) => updateSortOption(e.target.value)}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                aria-label="Sort by"
              >
                <option value="name">Name</option>
                <option value="registrationDate">Registration Date</option>
                <option value="town">Town</option>
              </select>
            </div>
          </div>

          {/* Select All Controls */}
          <div className="flex flex-col space-y-2 mb-4">
            <div className={`flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-700 rounded`}>
              <input
                type="checkbox"
                checked={filteredMembers.length > 0 && filteredMembers.every(member => selectedMembers.includes(member.id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    selectAllVisibleMembers();
                  }
                }}
                className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500`}
                aria-label="Select all visible family members for SMS sending"
              />
              <label className={`text-sm font-medium ${darkModeClasses.textPrimary} cursor-pointer`}>
                Select All Visible Members
              </label>
            </div>

            {messageType !== 'send-credentials' && !messageType.includes('credentials') && (
              <div className={`flex items-center space-x-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded`}>
                <input
                  type="checkbox"
                  checked={members.filter(m => m.phone && m.phone.trim() !== '').every(member => selectedMembers.includes(member.id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectAllMembers();
                    }
                  }}
                  className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500`}
                  aria-label="Select all family members with phone numbers from all towns for SMS sending"
                />
                <label className={`text-sm font-medium ${darkModeClasses.textPrimary} cursor-pointer`}>
                  Select All Members (All Towns)
                </label>
              </div>
            )}
          </div>

          {/* Member List */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredMembers.map((member: Member) => {
              const town = towns.find((t: Town) => t.id === member.townId);
              const isSelected = selectedMembers.includes(member.id);
              const isDisabled = messageType === 'send-credentials' || messageType.includes('credentials');

              return (
                <label key={member.id} className={`flex items-center space-x-3 p-2 ${darkModeClasses.hover} rounded ${isDisabled ? 'opacity-60' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected || !!(isDisabled && member.phone && member.phone.trim() !== '')}
                    onChange={() => toggleMemberSelection(member.id)}
                    disabled={isDisabled}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isDisabled ? 'cursor-not-allowed' : ''}`}
                    aria-label={`Select ${member.name} for SMS sending`}
                  />
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${darkModeClasses.textPrimary}`}>{member.name}</p>
                    <p className={`text-xs ${darkModeClasses.textTertiary}`}>{member.phone} • {town?.name}</p>
                  </div>
                </label>
              );
            })}
          </div>

          {filteredMembers.length === 0 && (
            <p className={`text-sm ${darkModeClasses.textSecondary} text-center py-4`}>
              No members found matching the current filters.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
